import { useCallback, useMemo, type MouseEvent } from 'react'

import { useSelector } from 'react-redux'
import { browserHistory } from 'react-router'
import useSWR from 'swr'

import { menuFetcher, type MenuQueryInput } from '@library/api-menu-experience'
import type { CollectionSlug, CollectionUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import type { SelectCollectionEvent } from 'routes/Menu/MenuExperience/MenuPage/types'
import { getBasketDate, getNumPortions } from 'selectors/basket'

import type { MenuQueryParams } from '../../context/menuQueryContext'
import { getMenuExperiencePreview } from '../../context/previewContext'

type MenuExperienceQuery = MenuQueryParams & Record<string, unknown> & { collection?: string }

export type MenuExperienceCategorySelectionMethod =
  SelectCollectionEvent['properties']['navigation_method']

type MenuExperienceMenuSWRKey = {
  key: 'menu-experience-menu'
  input: MenuQueryInput
}

export type MenuExperienceNavItem = {
  id: string
  text: string
  slug: string
  selected: boolean
  onClick: (event?: MouseEvent<HTMLElement>) => void
}

export type MenuExperienceCategoryNavigationTarget = {
  id: string
  slug: string
}

type CategoryAnalyticsProperties = Pick<SelectCollectionEvent['properties'], 'merch_header_type'>

export type MenuExperienceCategorySelect = (
  category: MenuExperienceCategoryNavigationTarget,
  navigationMethod: MenuExperienceCategorySelectionMethod,
  parentCategoryId?: string,
  analyticsProperties?: CategoryAnalyticsProperties,
) => void

const fetchMenuExperience = ({ input }: MenuExperienceMenuSWRKey) => menuFetcher(input)

const getSelectedCategorySlug = (query: MenuExperienceQuery) =>
  typeof query.collection === 'string' && query.collection.trim().length > 0
    ? query.collection
    : undefined

export const useMenuExperienceNavigation = (query: MenuExperienceQuery, pathname = '/menu') => {
  const deliveryDate = useSelector(getBasketDate)
  const numPortions = useSelector(getNumPortions)
  const preview = getMenuExperiencePreview(query)
  const selectedCategorySlug = getSelectedCategorySlug(query)

  const menuSWRKey: MenuExperienceMenuSWRKey | null =
    deliveryDate && numPortions
      ? {
          key: 'menu-experience-menu',
          input: {
            deliveryDate,
            numPortions,
            ...(selectedCategorySlug && { categorySlug: selectedCategorySlug }),
            ...(preview && { preview }),
          },
        }
      : null

  const { data: menuData, error: menuError } = useSWR(menuSWRKey, fetchMenuExperience)

  const selectedNavSlug = selectedCategorySlug ?? menuData?.categorySlug

  const currentCategoryId = menuData?.categoryId
  const currentCategorySlug = menuData?.categorySlug

  const selectCategory: MenuExperienceCategorySelect = useCallback(
    (category, navigationMethod, parentCategoryId, analyticsProperties) => {
      queueTrackingEvent<SelectCollectionEvent>({
        action: 'select_collection',
        properties: {
          collection_id: category.id as CollectionUUID,
          collection_slug: category.slug as CollectionSlug,
          ...(currentCategoryId &&
            currentCategorySlug && {
              current_collection_id: currentCategoryId as CollectionUUID,
              current_collection_slug: currentCategorySlug as CollectionSlug,
            }),
          ...(parentCategoryId && { parent_collection_id: parentCategoryId as CollectionUUID }),
          ...analyticsProperties,
          navigation_method: navigationMethod,
        },
      })

      browserHistory.push({
        pathname,
        query: {
          ...query,
          collection: category.slug,
        },
      })
    },
    [currentCategoryId, currentCategorySlug, pathname, query],
  )

  const links = useMemo<MenuExperienceNavItem[]>(
    () =>
      (menuData?.categories ?? []).map((category) => ({
        id: category.id,
        text: category.title,
        slug: category.slug,
        selected: category.slug === selectedNavSlug,
        onClick: (event) => {
          event?.preventDefault()
          selectCategory(category, 'navigation_bar')
        },
      })),
    [menuData?.categories, selectCategory, selectedNavSlug],
  )

  return {
    data: menuData,
    error: !menuData ? menuError : undefined,
    selectCategory,
    links,
  }
}
